import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { ChartLine, MagnifyingGlass, TrendUp, Briefcase, Sparkle, SignOut, User, Coins, Crown, Lightning, Compass, Flask, Wallet, BellRinging } from '@phosphor-icons/react';

const TIER_BADGE = {
  free: { label: 'FREE', color: '#94A3B8', icon: null },
  pro: { label: 'PRO', color: '#A855F7', icon: Lightning },
  elite: { label: 'ELITE', color: '#FBBF24', icon: Crown },
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { tier } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const badge = TIER_BADGE[tier] || TIER_BADGE.free;
  const BadgeIcon = badge.icon;

  return (
    <div className="min-h-screen noise-bg" style={{ background: '#0A0B0E' }}>
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0B0E]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="w-full max-w-[1920px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <ChartLine size={32} weight="duotone" className="text-[#2563EB]" />
              <h1 className="text-xl font-heading font-black tracking-tighter text-white">QuantoAura</h1>
            </div>

            <nav className="flex items-center gap-6">
              <Link
                to="/dashboard"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/dashboard') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-dashboard"
              >
                <TrendUp size={20} weight="duotone" />
                Dashboard
              </Link>
              <Link
                to="/screener"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/screener') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-screener"
              >
                <MagnifyingGlass size={20} weight="duotone" />
                Screener
              </Link>
              <Link
                to="/portfolio"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/portfolio') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-portfolio"
              >
                <Briefcase size={20} weight="duotone" />
                Portfolio
              </Link>
              <Link
                to="/options"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/options') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-options"
              >
                <Coins size={20} weight="duotone" />
                Options
              </Link>
              <Link
                to="/market-intel"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/market-intel') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-market-intel"
              >
                <Compass size={20} weight="duotone" />
                Market Intel
              </Link>
              <Link
                to="/paper-trading"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/paper-trading') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-paper-trading"
              >
                <Wallet size={20} weight="duotone" />
                Paper Trade
              </Link>
              <Link
                to="/backtest"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/backtest') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-backtest"
              >
                <Flask size={20} weight="duotone" />
                Backtest
              </Link>
              <Link
                to="/alerts"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/alerts') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-alerts"
              >
                <BellRinging size={20} weight="duotone" />
                Alerts
              </Link>
              <Link
                to="/ai-ideas"
                className={`text-sm transition-colors flex items-center gap-1 ${
                  isActive('/ai-ideas') ? 'text-white' : 'text-[#94A3B8] hover:text-white'
                }`}
                data-testid="nav-ai-ideas"
              >
                <Sparkle size={20} weight="duotone" />
                AI Ideas
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <Link
                to="/pricing"
                className="flex items-center gap-1.5 px-2 py-1 border text-xs font-mono font-bold hover:opacity-80 transition-opacity"
                style={{ borderColor: `${badge.color}50`, color: badge.color }}
                data-testid="tier-badge"
              >
                {BadgeIcon && <BadgeIcon size={14} weight="fill" />}
                {badge.label}
                {tier === 'free' && <span className="ml-1 text-[9px] uppercase tracking-widest">↑ Upgrade</span>}
              </Link>
              <div className="flex items-center gap-2 text-[#94A3B8] text-sm">
                <User size={20} weight="duotone" />
                <span data-testid="user-name">{user?.name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="text-[#94A3B8] hover:text-white transition-colors"
                data-testid="logout-button"
              >
                <SignOut size={20} weight="duotone" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
}
